<template>
  <hs-card class="ui-metric-card" :class="[`ui-metric-card--${tone}`]" padding="md">
    <div class="ui-metric-card__top">
      <div>
        <p>{{ label }}</p>
        <strong>{{ value }}</strong>
      </div>
      <hs-icon v-if="icon" :name="icon" :tone="tone" soft box-size="3rem" />
    </div>
    <div v-if="description || trend" class="ui-metric-card__bottom">
      <span v-if="description">{{ description }}</span>
      <hs-badge v-if="trend" :tone="trendTone" size="sm" :icon="trendIcon">
        {{ trend }}
      </hs-badge>
    </div>
  </hs-card>
</template>

<script>
import HsBadge from "./HsBadge.vue";
import HsCard from "./HsCard.vue";
import HsIcon from "./HsIcon.vue";

export default {
  name: "HsMetricCard",
  components: { HsBadge, HsCard, HsIcon },
  props: {
    label: {
      type: String,
      required: true,
    },
    value: {
      type: [String, Number],
      default: "--",
    },
    description: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "",
    },
    tone: {
      type: String,
      default: "primary",
      validator: (value) => ["neutral", "primary", "success", "warning", "danger", "info"].includes(value),
    },
    trend: {
      type: String,
      default: "",
    },
    trendDirection: {
      type: String,
      default: "up",
      validator: (value) => ["up", "down", "flat"].includes(value),
    },
  },
  computed: {
    trendTone() {
      if (this.trendDirection === "down") return "danger";
      if (this.trendDirection === "flat") return "neutral";
      return "success";
    },
    trendIcon() {
      if (this.trendDirection === "down") return "trending-down";
      if (this.trendDirection === "flat") return "minus";
      return "trending-up";
    },
  },
};
</script>

<style scoped>
.ui-metric-card {
  overflow: hidden;
}

.ui-metric-card::after {
  border-radius: 999px;
  content: "";
  height: 82px;
  opacity: 0.16;
  position: absolute;
  right: -24px;
  top: -26px;
  width: 82px;
}

.ui-metric-card--primary::after {
  background: var(--hs-primary, #3293ec);
}

.ui-metric-card--success::after {
  background: #22c55e;
}

.ui-metric-card--warning::after {
  background: #f59e0b;
}

.ui-metric-card--danger::after {
  background: #ef4444;
}

.ui-metric-card--info::after {
  background: #06b6d4;
}

.ui-metric-card__top {
  align-items: flex-start;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  position: relative;
  z-index: 1;
}

.ui-metric-card p {
  color: var(--hs-muted, #64748b);
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.11em;
  margin: 0 0 0.4rem;
  text-transform: uppercase;
}

.ui-metric-card strong {
  color: var(--hs-text, #0f172a);
  display: block;
  font-size: clamp(1.45rem, 2vw, 2.1rem);
  font-weight: 950;
  letter-spacing: 0;
  line-height: 1;
}

.ui-metric-card__bottom {
  align-items: center;
  display: flex;
  gap: 0.7rem;
  justify-content: space-between;
  margin-top: 1rem;
  position: relative;
  z-index: 1;
}

.ui-metric-card__bottom span:first-child {
  color: var(--hs-muted, #64748b);
  font-size: 0.82rem;
  font-weight: 800;
}
</style>
